import { API_PATH, BASE_PATH } from './config';
/**
 * DayManagement.jsx - Simplified Day Activity View
 * 
 * Clean, focused design showing the day's activities with clear visual feedback
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, Paper, IconButton, Button, Chip, LinearProgress,
    TextField, Snackbar, Alert, Dialog, DialogTitle, DialogContent, 
    DialogActions, useMediaQuery, useTheme, Tooltip, CircularProgress,
    AppBar, Toolbar, Collapse
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    AccessTime as AccessTimeIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Navigation as NavigationIcon,
    LocationOn as LocationOnIcon,
    DirectionsWalk as DirectionsWalkIcon,
    DirectionsCar as DirectionsCarIcon,
    FlightTakeoff as FlightTakeoffIcon,
    Hotel as HotelIcon,
    Restaurant as RestaurantIcon,
    LocalActivity as LocalActivityIcon,
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Map as MapIcon,
    Explore as ExploreIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Comprehensive timezone mapping for cities worldwide
const getTimezoneForDestination = (destination) => {
    if (!destination) return { tz: 'UTC', offset: '+0' };
    const lower = destination.toLowerCase();
    
    // Map of cities/countries to IANA timezone and GMT offset display
    const tzMap = {
        // Middle East
        'jerusalem': { tz: 'Asia/Jerusalem', offset: '+2' },
        'tel aviv': { tz: 'Asia/Jerusalem', offset: '+2' },
        'israel': { tz: 'Asia/Jerusalem', offset: '+2' },
        'dubai': { tz: 'Asia/Dubai', offset: '+4' },
        
        // Asia
        'vietnam': { tz: 'Asia/Ho_Chi_Minh', offset: '+7' },
        'hanoi': { tz: 'Asia/Ho_Chi_Minh', offset: '+7' },
        'ho chi minh': { tz: 'Asia/Ho_Chi_Minh', offset: '+7' },
        'saigon': { tz: 'Asia/Ho_Chi_Minh', offset: '+7' },
        'bangkok': { tz: 'Asia/Bangkok', offset: '+7' },
        'thailand': { tz: 'Asia/Bangkok', offset: '+7' },
        'phuket': { tz: 'Asia/Bangkok', offset: '+7' },
        'chiang mai': { tz: 'Asia/Bangkok', offset: '+7' },
        'pattaya': { tz: 'Asia/Bangkok', offset: '+7' },
        'krabi': { tz: 'Asia/Bangkok', offset: '+7' },
        'koh samui': { tz: 'Asia/Bangkok', offset: '+7' },
        'singapore': { tz: 'Asia/Singapore', offset: '+8' },
        'hong kong': { tz: 'Asia/Hong_Kong', offset: '+8' },
        'beijing': { tz: 'Asia/Shanghai', offset: '+8' },
        'shanghai': { tz: 'Asia/Shanghai', offset: '+8' },
        'china': { tz: 'Asia/Shanghai', offset: '+8' },
        'tokyo': { tz: 'Asia/Tokyo', offset: '+9' },
        'japan': { tz: 'Asia/Tokyo', offset: '+9' },
        'seoul': { tz: 'Asia/Seoul', offset: '+9' },
        'korea': { tz: 'Asia/Seoul', offset: '+9' },
        'mumbai': { tz: 'Asia/Kolkata', offset: '+5:30' },
        'delhi': { tz: 'Asia/Kolkata', offset: '+5:30' },
        'india': { tz: 'Asia/Kolkata', offset: '+5:30' },
        
        // Europe
        'london': { tz: 'Europe/London', offset: '+0' },
        'uk': { tz: 'Europe/London', offset: '+0' },
        'paris': { tz: 'Europe/Paris', offset: '+1' },
        'france': { tz: 'Europe/Paris', offset: '+1' },
        'berlin': { tz: 'Europe/Berlin', offset: '+1' },
        'germany': { tz: 'Europe/Berlin', offset: '+1' },
        'rome': { tz: 'Europe/Rome', offset: '+1' },
        'italy': { tz: 'Europe/Rome', offset: '+1' },
        'amsterdam': { tz: 'Europe/Amsterdam', offset: '+1' },
        'madrid': { tz: 'Europe/Madrid', offset: '+1' },
        'spain': { tz: 'Europe/Madrid', offset: '+1' },
        'barcelona': { tz: 'Europe/Madrid', offset: '+1' },
        'lisbon': { tz: 'Europe/Lisbon', offset: '+0' },
        'portugal': { tz: 'Europe/Lisbon', offset: '+0' },
        'athens': { tz: 'Europe/Athens', offset: '+2' },
        'greece': { tz: 'Europe/Athens', offset: '+2' },
        'moscow': { tz: 'Europe/Moscow', offset: '+3' },
        'russia': { tz: 'Europe/Moscow', offset: '+3' },
        
        // North America
        'new york': { tz: 'America/New_York', offset: '-5' },
        'nyc': { tz: 'America/New_York', offset: '-5' },
        'boston': { tz: 'America/New_York', offset: '-5' },
        'miami': { tz: 'America/New_York', offset: '-5' },
        'washington': { tz: 'America/New_York', offset: '-5' },
        'chicago': { tz: 'America/Chicago', offset: '-6' },
        'dallas': { tz: 'America/Chicago', offset: '-6' },
        'houston': { tz: 'America/Chicago', offset: '-6' },
        'denver': { tz: 'America/Denver', offset: '-7' },
        'phoenix': { tz: 'America/Phoenix', offset: '-7' },
        'los angeles': { tz: 'America/Los_Angeles', offset: '-8' },
        'san francisco': { tz: 'America/Los_Angeles', offset: '-8' },
        'seattle': { tz: 'America/Los_Angeles', offset: '-8' },
        'las vegas': { tz: 'America/Los_Angeles', offset: '-8' },
        'san diego': { tz: 'America/Los_Angeles', offset: '-8' },
        'vancouver': { tz: 'America/Vancouver', offset: '-8' },
        'toronto': { tz: 'America/Toronto', offset: '-5' },
        'montreal': { tz: 'America/Toronto', offset: '-5' },
        'canada': { tz: 'America/Toronto', offset: '-5' },
        'mexico city': { tz: 'America/Mexico_City', offset: '-6' },
        'cancun': { tz: 'America/Cancun', offset: '-5' },
        
        // South America
        'sao paulo': { tz: 'America/Sao_Paulo', offset: '-3' },
        'rio': { tz: 'America/Sao_Paulo', offset: '-3' },
        'brazil': { tz: 'America/Sao_Paulo', offset: '-3' },
        'buenos aires': { tz: 'America/Argentina/Buenos_Aires', offset: '-3' },
        'argentina': { tz: 'America/Argentina/Buenos_Aires', offset: '-3' },
        'lima': { tz: 'America/Lima', offset: '-5' },
        'peru': { tz: 'America/Lima', offset: '-5' },
        
        // Oceania
        'sydney': { tz: 'Australia/Sydney', offset: '+10' },
        'melbourne': { tz: 'Australia/Melbourne', offset: '+10' },
        'australia': { tz: 'Australia/Sydney', offset: '+10' },
        'auckland': { tz: 'Pacific/Auckland', offset: '+12' },
        'new zealand': { tz: 'Pacific/Auckland', offset: '+12' },
        
        // Africa
        'cairo': { tz: 'Africa/Cairo', offset: '+2' },
        'egypt': { tz: 'Africa/Cairo', offset: '+2' },
        'johannesburg': { tz: 'Africa/Johannesburg', offset: '+2' },
        'south africa': { tz: 'Africa/Johannesburg', offset: '+2' },
        'nairobi': { tz: 'Africa/Nairobi', offset: '+3' },
        'kenya': { tz: 'Africa/Nairobi', offset: '+3' },
        'morocco': { tz: 'Africa/Casablanca', offset: '+1' },
        'casablanca': { tz: 'Africa/Casablanca', offset: '+1' }
    };
    
    for (const [city, info] of Object.entries(tzMap)) {
        if (lower.includes(city)) return info;
    }
    return { tz: 'UTC', offset: '+0' };
};

// Navigation URL builders
const getGoogleMapsUrl = (activity) => {
    const loc = activity.location || activity.address;
    if (!loc) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc)}`;
};

const getWazeUrl = (activity) => {
    const loc = activity.location || activity.address;
    if (!loc) return null;
    return `https://waze.com/ul?q=${encodeURIComponent(loc)}&navigate=yes`;
};

const getAppleMapsUrl = (activity) => {
    const loc = activity.location || activity.address;
    if (!loc) return null;
    return `https://maps.apple.com/?daddr=${encodeURIComponent(loc)}&dirflg=d`;
};

// Get icon for activity type
const getActivityIcon = (activity) => {
    const type = activity?.type || '';
    const subtype = activity?.subtype || '';
    
    if (type === 'lodging') return <HotelIcon />;
    if (subtype === 'flight') return <FlightTakeoffIcon />;
    if (subtype === 'walk') return <DirectionsWalkIcon />;
    if (subtype === 'car') return <DirectionsCarIcon />;
    if (type === 'meal') return <RestaurantIcon />;
    return <LocalActivityIcon />;
};

// Get color for activity type
const getActivityColor = (activity) => {
    const type = activity?.type || '';
    const subtype = activity?.subtype || '';
    
    if (type === 'lodging') return '#8b5cf6';
    if (subtype === 'flight') return '#3b82f6';
    if (type === 'meal') return '#ef4444';
    return '#6366f1';
};

// Calculate end time
const getEndTime = (startTime, duration) => {
    if (!startTime) return '--:--';
    const [h, m] = startTime.split(':').map(Number);
    const total = h * 60 + m + (duration || 60);
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// Check activity status - localTimeDayjs is a dayjs object in destination timezone
const getActivityStatus = (activity, localTimeDayjs, dayDate) => {
    if (activity.is_completed || activity.completed_at) return 'completed';
    if (!activity.start_time || !localTimeDayjs) return 'upcoming';
    
    const today = localTimeDayjs.format('YYYY-MM-DD');
    
    // Only check if viewing today
    if (dayDate !== today) return 'upcoming';
    
    const startMinutes = parseInt(activity.start_time.split(':')[0]) * 60 + parseInt(activity.start_time.split(':')[1]);
    const endMinutes = startMinutes + (activity.duration || 60);
    const nowMinutes = localTimeDayjs.hour() * 60 + localTimeDayjs.minute();
    
    if (nowMinutes >= startMinutes && nowMinutes < endMinutes) return 'active';
    if (nowMinutes >= endMinutes) return 'past';
    return 'upcoming';
};

// Single Activity Component - Clean and focused
const ActivityItem = ({ 
    activity, 
    status, 
    onComplete, 
    onNavigate, 
    onEditNote,
    t 
}) => {
    const [expanded, setExpanded] = useState(status === 'active');
    const color = getActivityColor(activity);
    const endTime = getEndTime(activity.start_time, activity.duration);
    
    const statusColors = {
        completed: { bg: '#dcfce7', border: '#22c55e', text: '#15803d' },
        active: { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8' },
        past: { bg: '#f3f4f6', border: '#9ca3af', text: '#6b7280' },
        upcoming: { bg: '#ffffff', border: '#e5e7eb', text: '#374151' }
    };
    
    const colors = statusColors[status];

    return (
        <Paper
            elevation={0}
            sx={{
                mb: 2,
                border: '2px solid',
                borderColor: colors.border,
                borderRadius: 3,
                bgcolor: colors.bg,
                overflow: 'hidden',
                transition: 'all 0.2s ease'
            }}
        >
            {/* Main Row - Always visible */}
            <Box 
                sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Time Column */}
                    <Box sx={{ minWidth: 60, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text, lineHeight: 1.2 }}>
                            {activity.start_time?.slice(0, 5) || '--:--'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {endTime}
                        </Typography>
                    </Box>
                    
                    {/* Icon */}
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: `${color}20`,
                            color: color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        {getActivityIcon(activity)}
                    </Box>
                    
                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, overflow: 'hidden' }}>
                            <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                    fontWeight: 600, 
                                    color: colors.text,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textDecoration: status === 'completed' ? 'line-through' : 'none'
                                }}
                            >
                                {activity.title}
                            </Typography>
                            {status === 'completed' && (
                                <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                            )}
                        </Box>
                        
                        {(activity.location || activity.address) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {activity.location || activity.address}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    
                    {/* Expand Icon */}
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            </Box>
            
            {/* Expanded Section - Actions */}
            <Collapse in={expanded}>
                <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        pt: 2
                    }}>
                        {/* Navigation Apps */}
                        {(activity.location || activity.address) && (
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 2, px: 0.5 }}>
                                <Tooltip title="Google Maps">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); const url = getGoogleMapsUrl(activity); if (url) { window.open(url, '_blank'); onNavigate(activity); } }}
                                        sx={{ color: '#EA4335', '&:hover': { bgcolor: '#EA433515' } }}
                                    >
                                        <MapIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Waze">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); const url = getWazeUrl(activity); if (url) { window.open(url, '_blank'); onNavigate(activity); } }}
                                        sx={{ color: '#33CCFF', '&:hover': { bgcolor: '#33CCFF15' } }}
                                    >
                                        <NavigationIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Apple Maps">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); const url = getAppleMapsUrl(activity); if (url) { window.open(url, '_blank'); onNavigate(activity); } }}
                                        sx={{ color: '#555', '&:hover': { bgcolor: '#55555515' } }}
                                    >
                                        <ExploreIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        
                        {/* Complete/Completed Button */}
                        {status !== 'completed' ? (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircleIcon />}
                                onClick={(e) => { e.stopPropagation(); onComplete(activity); }}
                                sx={{ 
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    bgcolor: '#22c55e',
                                    '&:hover': { bgcolor: '#16a34a' }
                                }}
                            >
                                {t('day_mgmt_mark_done')}
                            </Button>
                        ) : (
                            <Chip 
                                icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                label={t('day_mgmt_completed')}
                                size="small"
                                sx={{ bgcolor: '#dcfce7', color: '#15803d' }}
                            />
                        )}
                        
                        {/* Add Note Button */}
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<EditIcon />}
                            onClick={(e) => { e.stopPropagation(); onEditNote(activity); }}
                            sx={{ 
                                textTransform: 'none',
                                borderRadius: 2,
                                color: 'text.secondary'
                            }}
                        >
                            {t('day_mgmt_add_note')}
                        </Button>
                    </Box>
                    
                    {/* Notes Display */}
                    {activity.notes && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fef3c7', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ color: '#92400e' }}>
                                📝 {activity.notes}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

// Progress Summary Component
const ProgressSummary = ({ activities, t }) => {
    const completed = activities.filter(a => a.is_completed || a.completed_at).length;
    const total = activities.length;
    const progress = total > 0 ? (completed / total) * 100 : 0;
    
    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {t('day_mgmt_today_progress')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: progress === 100 ? '#22c55e' : '#3b82f6' }}>
                    {completed} / {total}
                </Typography>
            </Box>
            <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    bgcolor: '#e5e7eb',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        bgcolor: progress === 100 ? '#22c55e' : '#3b82f6'
                    }
                }}
            />
            {progress === 100 && (
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: '#22c55e', fontWeight: 600 }}>
                    🎉 {t('day_mgmt_all_done')}
                </Typography>
            )}
        </Box>
    );
};

// Main Component
const DayManagement = () => {
    const { tripId, dayDate } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // State
    const [tripData, setTripData] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [localTimeDisplay, setLocalTimeDisplay] = useState('--:--');
    const [localTimeDayjs, setLocalTimeDayjs] = useState(null);
    const [timezoneInfo, setTimezoneInfo] = useState({ tz: 'UTC', offset: '+0' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [noteDialog, setNoteDialog] = useState({ open: false, activity: null, text: '' });
    
    const activeRef = useRef(null);
    
    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };
            
            // Fetch trip
            const tripRes = await fetch(`${API_PATH}/trips/get_single.php?trip_id=${tripId}`, { headers });
            const tripJson = await tripRes.json();
            if (!tripRes.ok) {
                // If trip not found, redirect to home
                if (tripRes.status === 404) {
                    navigate('/');
                    return;
                }
                throw new Error(tripJson.message || 'Failed to fetch trip');
            }
            
            setTripData(tripJson);
            const tzInfo = getTimezoneForDestination(tripJson.destination || '');
            console.log('Trip destination:', tripJson.destination, '-> Timezone:', tzInfo.tz);
            setTimezoneInfo(tzInfo);
            
            // Fetch activities
            const actRes = await fetch(`${API_PATH}/activities.php?trip_id=${tripId}`, { headers });
            const actJson = await actRes.json();
            
            if (actRes.ok && Array.isArray(actJson)) {
                const tripStart = new Date(tripJson.start_date);
                const selectedDate = new Date(dayDate);
                const dayNumber = Math.floor((selectedDate - tripStart) / (1000 * 60 * 60 * 24)) + 1;
                
                const dayActivities = actJson
                    .filter(a => parseInt(a.day) === dayNumber)
                    .sort((a, b) => (a.start_time || '99:99').localeCompare(b.start_time || '99:99'));
                
                setActivities(dayActivities);
            }
            
            setError(null);
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [tripId, dayDate, navigate]);
    
    // Fetch server time and update every minute
    const fetchServerTime = useCallback(async () => {
        try {
            const res = await fetch(`${API_PATH}/server_time.php`);
            const data = await res.json();
            if (data.timestamp) {
                // Server returns UTC timestamp in seconds
                // Convert to destination timezone and format
                const localTime = dayjs.unix(data.timestamp).tz(timezoneInfo.tz);
                setLocalTimeDisplay(localTime.format('HH:mm'));
                setLocalTimeDayjs(localTime);
                console.log('Server UTC:', data.utc_datetime, '-> Local (' + timezoneInfo.tz + '):', localTime.format('HH:mm'));
            }
        } catch (err) {
            console.error('Error fetching server time:', err);
            // Fallback: use current time in destination timezone
            const fallbackTime = dayjs().tz(timezoneInfo.tz);
            setLocalTimeDisplay(fallbackTime.format('HH:mm'));
            setLocalTimeDayjs(fallbackTime);
        }
    }, [timezoneInfo.tz]);
    
    useEffect(() => {
        if (timezoneInfo.tz) {
            fetchServerTime();
            const interval = setInterval(fetchServerTime, 60000);
            return () => clearInterval(interval);
        }
    }, [timezoneInfo.tz, fetchServerTime]);
    
    // Fetch on mount
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Scroll to active activity
    useEffect(() => {
        if (activeRef.current) {
            setTimeout(() => {
                activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
    }, [activities]);
    
    // Mark activity as completed
    const handleComplete = async (activity) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_PATH}/activities/complete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ 
                    activity_id: activity.activity_id || activity.id, 
                    completed_at: new Date().toISOString() 
                })
            });
            
            // Update local state immediately for instant feedback
            setActivities(prev => prev.map(a => 
                (a.activity_id || a.id) === (activity.activity_id || activity.id) 
                    ? { ...a, is_completed: true, completed_at: new Date().toISOString() }
                    : a
            ));
            
            setSnackbar({ 
                open: true, 
                message: `✓ "${activity.title}" ${t('day_mgmt_marked_complete')}`, 
                severity: 'success' 
            });
        } catch (err) {
            console.error('Error:', err);
            setSnackbar({ open: true, message: t('day_mgmt_error'), severity: 'error' });
        }
    };
    
    // Navigate to activity — URL is built in ActivityItem; this just shows feedback
    const handleNavigate = (activity) => {
        setSnackbar({ 
            open: true, 
            message: `🗺️ ${t('day_mgmt_opening_maps')}`, 
            severity: 'info' 
        });
    };
    
    // Save note
    const handleSaveNote = async () => {
        if (!noteDialog.activity) {
            setNoteDialog({ open: false, activity: null, text: '' });
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_PATH}/activities/notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ 
                    activity_id: noteDialog.activity.activity_id || noteDialog.activity.id, 
                    note: noteDialog.text, 
                    trip_id: tripId 
                })
            });
            
            // Update local state
            setActivities(prev => prev.map(a => 
                (a.activity_id || a.id) === (noteDialog.activity.activity_id || noteDialog.activity.id)
                    ? { ...a, notes: noteDialog.text }
                    : a
            ));
            
            setSnackbar({ 
                open: true, 
                message: `📝 ${t('day_mgmt_note_saved')}`, 
                severity: 'success' 
            });
        } catch (err) {
            console.error('Error:', err);
        }
        
        setNoteDialog({ open: false, activity: null, text: '' });
    };
    
    // Get day info
    const getDayInfo = () => {
        if (!tripData) return { title: '', subtitle: '' };
        const tripStart = new Date(tripData.start_date);
        const selected = new Date(dayDate);
        const dayNum = Math.floor((selected - tripStart) / (1000 * 60 * 60 * 24)) + 1;
        return {
            title: `${t('day_mgmt_day')} ${dayNum}`,
            subtitle: dayjs(dayDate).format('dddd, MMMM D')
        };
    };
    
    const dayInfo = getDayInfo();
    
    // Loading state
    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh', 
                bgcolor: '#f8fafc',
                gap: 2
            }}>
                <CircularProgress size={40} />
                <Typography color="text.secondary">{t('day_mgmt_loading')}</Typography>
            </Box>
        );
    }
    
    // Error state
    if (error) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
                <Button variant="contained" onClick={fetchData}>{t('day_mgmt_retry')}</Button>
            </Box>
        );
    }
    
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Simple Header */}
            <AppBar 
                position="sticky" 
                elevation={0} 
                sx={{ 
                    bgcolor: 'white', 
                    borderBottom: '1px solid #e5e7eb'
                }}
            >
                <Toolbar>
                    <IconButton 
                        edge="start" 
                        onClick={() => navigate(`/trip/${tripId}`)}
                        sx={{ color: '#374151', mr: 1 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ color: '#111827', fontWeight: 700, lineHeight: 1.2 }}>
                            {dayInfo.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {dayInfo.subtitle}
                        </Typography>
                    </Box>
                    
                    <Chip
                        icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                        label={localTimeDisplay}
                        size="small"
                        sx={{ 
                            bgcolor: '#f3f4f6',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: '#6b7280' }
                        }}
                    />
                </Toolbar>
            </AppBar>
            
            {/* Main Content */}
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700, mx: 'auto' }}>
                {/* Progress */}
                <ProgressSummary activities={activities} t={t} />
                
                {/* Activities List */}
                {activities.length === 0 ? (
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 4, 
                            textAlign: 'center', 
                            border: '2px dashed #e5e7eb',
                            borderRadius: 3,
                            bgcolor: 'white'
                        }}
                    >
                        <Typography variant="h6" sx={{ color: '#9ca3af', mb: 1 }}>
                            {t('day_mgmt_no_activities')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                            {t('day_mgmt_no_activities_hint')}
                        </Typography>
                    </Paper>
                ) : (
                    <Box>
                        {activities.map((activity, index) => {
                            const status = getActivityStatus(activity, localTimeDayjs, dayDate);
                            const isActive = status === 'active';
                            
                            return (
                                <Box 
                                    key={activity.activity_id || activity.id || index}
                                    ref={isActive ? activeRef : null}
                                >
                                    <ActivityItem
                                        activity={activity}
                                        status={status}
                                        onComplete={handleComplete}
                                        onNavigate={handleNavigate}
                                        onEditNote={(a) => setNoteDialog({ 
                                            open: true, 
                                            activity: a, 
                                            text: a.notes || '' 
                                        })}
                                        t={t}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
            
            {/* Note Dialog */}
            <Dialog 
                open={noteDialog.open} 
                onClose={() => setNoteDialog({ open: false, activity: null, text: '' })}
                fullWidth 
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {t('day_mgmt_add_note')}
                    {noteDialog.activity && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            {noteDialog.activity.title}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={4}
                        placeholder={t('day_mgmt_note_placeholder')}
                        value={noteDialog.text}
                        onChange={(e) => setNoteDialog(prev => ({ ...prev, text: e.target.value }))}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                        onClick={() => setNoteDialog({ open: false, activity: null, text: '' })}
                        sx={{ borderRadius: 2 }}
                    >
                        {t('day_mgmt_cancel')}
                    </Button>
                    <Button 
                        onClick={handleSaveNote} 
                        variant="contained"
                        sx={{ borderRadius: 2 }}
                    >
                        {t('day_mgmt_save')}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Snackbar for feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    sx={{ borderRadius: 2, fontWeight: 500 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DayManagement;
